import React, { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { useParams } from 'react-router-dom';
import { useStore } from '../store';
import { TestBadge } from '../service/tableService';
import Spinner from '@atlaskit/spinner';
import If from '../components/If';
import Button from '@atlaskit/button/new';
import { useHistory } from 'react-router-dom';
import AiService from '../service/aiService';
import { IconButton } from '@atlaskit/button/new';
import EditorCloseIcon from '@atlaskit/icon/glyph/editor/close';
import TextArea from '@atlaskit/textarea';
import PremiumIcon from '@atlaskit/icon/glyph/premium';
import { B300 } from '@atlaskit/theme/colors';


const AiPrompts = observer(() => {
    const { type, id, prompt } = useParams();
    const [newTests, setNewTests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pageTitle, setPageTitle] = useState('');
    const [text, setText] = useState('');
    const history = useHistory();
    const aiService = new AiService();
    const [title, setTitle] = useState(null);


    const requestPrompt = useCallback(async () => {
        setLoading(true);
        
        try {
            const fetchPrompts = await aiService.getPrompts({type, id, prompt});
            if (prompt === 'suggest_new_tests') {
                setNewTests(fetchPrompts.models.filter(model => model.type === 'test' && model.status === 'add'))
            } else {
                const editAttribute = fetchPrompts.options.edit;
                setText(fetchPrompts.data[editAttribute])
            }

            setPageTitle(fetchPrompts.options?.title || 'AI Suggestions')
            if (!(fetchPrompts.options?.title || 'AI Suggestions').includes(':')) setTitle(fetchPrompts.models.find(t => t.id === id).title || null)
        } catch (error) {
            //
        } finally {
            setLoading(false);
        }
    }, [type, id, prompt, aiService]);

    useEffect(() => {
        requestPrompt()
        // eslint-disable-next-line
    }, []);

    const TestOrSuite = observer(({ test }) => {
        const [loading, setLoading] = useState(false);
        const [isRemove, setIsRemove] = useState(false);
        const { jira } = useStore();

        const addTest = useCallback(async () => {
            setLoading(true);
            try {
                const res = await aiService.createItem(test);
                if (res.data.id) test.id = res.data.id;
                jira.toast = {
                    type: 'success',
                    message: 'Test added successfully.',
                    link: `${jira.testomatioURL}/projects/${jira.testomatioProject.slug}/test/${res.data.id}`,
                }
            } catch {
                //
            } finally {
            setLoading(false);
            }
        }, [test, jira]);

        return <div className='flex items-center gap-2 mb-1 leading-7'>
                    <span className={`truncate ${isRemove ? 'line-through' : ''}`}>
                        {test.title}
                    </span>
                    <TestBadge type='manual' />
                    <If condition={!test.id && !isRemove}>
                        <Button
                            size="small"
                            appearance="primary"
                            sizes="small"
                            spacing="compact"
                            onClick={() => addTest(test)}
                            isLoading={loading}
                        >
                            Add
                        </Button>
                        <IconButton
                            appearance="subtle"
                            sizes="small"
                            spacing="compact"
                            icon={EditorCloseIcon}
                            label="Remove"
                            onClick={() => setIsRemove(true)}
                            
                        >
                        </IconButton>
                    </If>
                </div>
    })

    const UpdateDescription = observer(({ description, type,id }) => {

        const [val, setVal] = useState(description);
        const [loading, setLoading] = useState(false);
        const history = useHistory();
        const { jira } = useStore();

        const saveValue = useCallback(async () => {
            setLoading(true);
            try {
                const data = await jira.testomatioRequest(`/${type}s/${id}`, {
                    method: "GET"
                })
                data.data.attributes.description = val;
                await jira.testomatioRequest(`/${type}s/${id}`, {
                    method: 'PUT',
                    body: JSON.stringify(data)
                })
                jira.toast = {
                    type: 'success',
                    message: `${type} description updated`,
                    link: `${jira.testomatioURL}/projects/${jira.testomatioProject.slug}/${type}/${data.data.id}`,
                }
                history.goBack();
            } catch (error) {
                jira.toast = {
                    type: 'error',
                    message: 'Failed to update suite description'
                }
            } finally {
                setLoading(false);
            }
        }, [val, type, id, history, jira])

        return <>
                <TextArea
                    name="ref"
                    label="ref"
                    placeholder="Enter config here"
                    value={val}
                    onChange={(e) => setVal(e.target.value)}
                    
                />
                <div className='mt-4 flex gap-2'>
                    <Button appearance="primary" isLoading={loading} onClick={() => saveValue()}>Save</Button>
                    <Button appearance="default" onClick={() => history.goBack()}>Cancel</Button>
                </div>
        </>
    })

    return (
        <>
            <If condition={loading}>
                <div className="flex justify-center mt-32">
                    <Spinner size="large" />
                </div>
            </If>
            <If condition={!loading}>
                <div className="flex mb-4 pb-2 items-center shadow-sm justify-between">
                    <div className='flex items-center gap-2'>
                        <PremiumIcon size="medium" label='' primaryColor={B300} />
                        <span>
                            {pageTitle}{title ? `: ${title}` : ''}
                        </span>
                    </div>
                    <Button appearance="default" onClick={() => history.goBack()}>
                        Back
                    </Button>
                </div>
                <If condition={prompt === 'suggest_new_tests'}>
                    <If condition={!!newTests?.length}>
                        {newTests.map(test => {
                            return <TestOrSuite test={test} />
                        })}
                    </If>
                    <If condition={!newTests?.length}>
                        <div className="mt-32 text-center">
                           There are no suggested tests
                        </div>
                    </If>
                </If>
                <If condition={prompt !== 'suggest_new_tests'}>
                    <UpdateDescription description={text} type={type} id={id} />
                </If>
            </If>
        </>
    );
});

export default AiPrompts;